"use client";
import { GET_ASSOCIATIONS } from "@/backend-graphql/queries/backend-graph-queries";
import NoDataDisplay from "@/components/NoDataDisplay";
import ImageWithFallback from "@/layouts/ImageFallBack";
import { useStateRegionStore } from "@/store/store";
import { LoadingSpinner } from "@/utils/components/LoadingSpinner";
import { getNationalId, getS3URL } from "@/utils/utilities";
import { useQuery } from "@apollo/client";
import { AssociationCard, CardData } from "@nrldigitaltechnology/ui-community";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useState, useTransition } from "react";
import { useStore } from "zustand";

type RegionClientProps = {
  regionId: string;
};

const RegionClient = ({ regionId }: RegionClientProps) => {
  // const params = useParams();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { state, region, setAssociation: setAssociationState } = useStateRegionStore()

  const [isPending, startTransition] = useTransition();
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const type = searchParams.get("type");
  // const locationId = searchParams.get("locationId");
  // const regionLogo = state?.logo;
  const regionName = searchParams.get("label");

  const id = regionId;
  const nationalId = getNationalId();
  const { data: associationsRes, loading: associationsLoading, error } = useQuery(
    GET_ASSOCIATIONS,
    {
      variables: {
        regionId: Number(id),
        nationalId: nationalId,
      },
      errorPolicy: "all",
    }
  );
  const [association, setAssociation] = useState({
    selectedAssociation: {},
    associationOptions: [],
  });

  useEffect(() => {
    if (associationsRes?.associationsByRegion) {
      const framedAssociation = associationsRes?.associationsByRegion.map(
        (association: any) => ({
          _id: association._id,
          name: association.name,
          heading: "Association",
          state: association?.orgtree?.state?.name,
          logo: association?.meta?.avatar || "/assets/logo/noimage.png",
        })
      );
      setAssociation((prev) => ({
        ...prev,
        associationOptions: framedAssociation,
      }));
    }
  }, [associationsRes]);

  const handleCardClick = (item: CardData) => {
    setAssociationState({
      label: item.name,
      value: item._id.toString(),
      logo: item.logo || "/assets/logo/noimage.png",
    });
    setLoadingId(item._id?.toString());
    const path = `/competitions/association/${item._id
      }?label=${encodeURIComponent(item.name)}&type=association`;
    startTransition(() => {
      router.push(path);
    });
  };

  return (
    <div className="flex w-full flex-col items-center bg-gray-100">
      <div className="w-full flex flex-col items-center my-10">
        {region?.logo && (
          <div data-an-module="association-container" className=" flex w-full justify-start items-center gap-2 mb-2">
            <div className="ml-1 bg-white p-2 rounded-md">
              <ImageWithFallback
                style="w-10 h-10"
                src={region?.logo}
                fallbackSrc={"/assets/logo/noimage.png"}
                alt=" "
              />
            </div>
            <p data-an-value-name="association-name" className="text-lg font-semibold">{region?.label}</p>
          </div>
        )}
        {associationsLoading ? (
          <LoadingSpinner text="Loading..." />
        ) : (
          <>
            {association?.associationOptions?.length === 0 ?
              <NoDataDisplay dataSearch="Association" /> :

              <div data-an-module="association-container" className="w-full grid grid-cols-1 md:grid-cols-2  lg:grid-cols-3 gap-3 sm:gap-7">
                {association?.associationOptions?.map((item: CardData, index) => (
                  <div className="relative" key={`${item._id}_${index}`}>
                    {loadingId === item._id.toString() && (
                      <div className="bg-black/50 absolute inset-0 flex items-center justify-center">
                        <LoadingSpinner theme="light" />{" "}
                      </div>
                    )}

                    <button
                      data-an-el="association-card"
                      className={`block w-full text-left ${loadingId === item._id.toString()
                        ? "opacity-100"
                        : "opacity-100"
                        }`}
                      onClick={() => handleCardClick(item)}
                    >
                      <AssociationCard data-an-el-subtype="association-card" cardData={item} />
                    </button>
                  </div>
                ))}
              </div>
            }
          </>
        )}
      </div>
    </div>
  );
};

export default RegionClient;
